import PwaInstall, {
  type PwaInstallHandle,
} from "@aitisal/ui/common/PwaInstall";
import { CheckoutHeader } from "@aitisal/ui/common/CheckoutHeader";
import {
  Outlet,
  createRootRoute,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import React, { useCallback, useEffect, useRef } from "react";
import CheckoutFooter from "../components/Footer/CheckoutFooter";
import { NewsTicker } from "../components/NewsTicker";
import { useTheme } from "../themes/ThemeProvider";
import { ThemeSlot } from "../themes/slots";
import { useSession } from "../stores/sessionStore";
import { useRestuarantDetails } from "../stores/stores";
import { useBrandingConfig } from "../utils/brandingConfig";

export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: () => <NotFoundComponent />,
});

const NotFoundComponent = () => {
  const navigate = useNavigate();
  useEffect(() => {
    navigate({ to: "/", replace: true });
  }, [navigate]);
  return null;
};

function RootComponent() {
  const pwaInstallRef = useRef<PwaInstallHandle>(null);
  const { setBrandId } = useSession() as any;
  const { restaurant } = useRestuarantDetails();
  const brandingConfig = useBrandingConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const { themeId } = useTheme();
  // Checkout keeps a minimal footer (Powered by Aitisal + ©) — the full
  // theme footer is reserved for the browsing routes.
  const isCheckout = location.pathname.endsWith("/checkout");

  // Set brandId from meta tag on app initialization (same as vendor app)
  useEffect(() => {
    const metaBrandId = document
      .querySelector('meta[name="brand"]')
      ?.getAttribute("content");
    if (metaBrandId) {
      setBrandId(metaBrandId);
    }
  }, [setBrandId]);

  const handleInstallSuccess = useCallback((event: CustomEvent) => {
    console.log("PWA installed successfully!", event.detail);
  }, []);

  const handleInstallFail = useCallback((event: CustomEvent) => {
    console.log("PWA installation failed.", event.detail);
  }, []);

  const handleInstallAvailable = useCallback((event: CustomEvent) => {
    console.log("PWA is available for installation.", event.detail);
  }, []);

  const handleUserChoiceResult = useCallback((event: CustomEvent) => {
    console.log("User choice result:", event.detail);
  }, []);

  return (
    <React.Fragment>
      <PwaInstall
        ref={pwaInstallRef}
        manualChrome={true}
        manualApple={true}
        onInstallSuccess={handleInstallSuccess}
        onInstallFail={handleInstallFail}
        onInstallAvailable={handleInstallAvailable}
        onUserChoiceResult={handleUserChoiceResult}
      />
      {/* The checkout page and the elo theme show no news ticker — elo's
          dynamic note strip in its header is the ticker slot. */}
      {!isCheckout && themeId !== "elo" && <NewsTicker />}
      {isCheckout ? (
        <CheckoutHeader
          logoSrc={brandingConfig.logo}
          logoAlt={brandingConfig.title}
          onLogoClick={() => navigate({ to: "/c" })}
          whatsappNumber={
            restaurant?.phone || brandingConfig.phoneNumber || ""
          }
        />
      ) : (
        <ThemeSlot slot="header" />
      )}
      <Outlet />
      {isCheckout ? <CheckoutFooter /> : <ThemeSlot slot="footer" />}
    </React.Fragment>
  );
}
